import { useState, useMemo, useEffect, useRef } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import {
  useFetchAutocompleteOptionsQuery,
  AutocompleteOption,
  ProfileValueOption,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Autocomplete, Box, Chip, SxProps, TextField, Theme } from '@mui/material';
import { Controller, Control, ControllerRenderProps, useFormContext } from 'react-hook-form';

import { ProfileFieldConfig } from './types/profile.types';

type ProfileAutoCompleteFieldProps = Readonly<{
  field: ProfileFieldConfig;
  control: Control;
  disabled?: boolean;
  error?: string;
}>;

const handleAsInstitutionOption = (field: ProfileFieldConfig) =>
  field.type.variant === 'API_AUTOCOMPLETE' && field.type?.expectInstitutionalOptions;

const processInitialValueObjectAsAutocompleteOption = (
  value: Record<string, unknown>,
  field: ProfileFieldConfig,
): AutocompleteOption => {
  const configuredDisplayProperty =
    field.type.variant === 'API_AUTOCOMPLETE' ? field.type.displayProperty : undefined;

  let displayValue: string;

  // Use configured displayProperty if available and present on the object
  if (configuredDisplayProperty && configuredDisplayProperty in value) {
    const propValue = (value as Record<string, string | number>)[configuredDisplayProperty];

    displayValue = String(propValue);
  }
  // Handle ProfileValueOption (has 'name' property)
  else if ('name' in value) {
    displayValue = (value as ProfileValueOption).name;
  }
  // Handle AutocompleteOption (has 'label' or 'value' property)
  else if ('label' in value) {
    displayValue = (value as AutocompleteOption).label;
  } else if ('value' in value && (value as AutocompleteOption).value) {
    displayValue = (value as AutocompleteOption).value;
  }
  // Fallback to string conversion
  else {
    displayValue = String(value);
  }

  const objectValue = {
    ...(value as Record<string, unknown>), // Preserve all additional properties first
    label: displayValue, // Override with computed display value
    value: displayValue, // Override with computed display value
  } as AutocompleteOption;

  return objectValue;
};

export const ProfileApiAutoCompleteField = ({
  field,
  control,
  disabled,
  error,
}: ProfileAutoCompleteFieldProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { reset } = useFormContext();
  const hasAppliedInitialValue = useRef(false);
  const minSearchLength =
    field.type.variant === 'API_AUTOCOMPLETE' ? (field.type.minSearchLength ?? 4) : 4;

  const { data: autocompleteResults } = useFetchAutocompleteOptionsQuery(
    {
      endpoint: field.type.variant === 'API_AUTOCOMPLETE' ? field.type.autoCompleteEndpoint : '',
      searchTerm,
      displayProperty:
        field.type.variant === 'API_AUTOCOMPLETE' ? field.type.displayProperty : undefined,
      filter: field.type.variant === 'API_AUTOCOMPLETE' ? field.type.filter : undefined,
      minSearchLength:
        field.type.variant === 'API_AUTOCOMPLETE' ? field.type.minSearchLength : undefined,
    },
    {
      skip:
        !searchTerm ||
        searchTerm.length < minSearchLength ||
        field.type.variant !== 'API_AUTOCOMPLETE',
    },
  );

  const formattedOptions = useMemo(() => {
    if (!autocompleteResults?.length) return [];

    const options = autocompleteResults[0]?.values ?? [];

    return options;
  }, [autocompleteResults]);

  const initialValue = (): AutocompleteOption | AutocompleteOption[] | null => {
    if (handleAsInstitutionOption(field)) {
      if (field.defaultValue && Array.isArray(field.defaultValue)) {
        const values = field.defaultValue.map((value) => {
          if (typeof value === 'string') {
            return { label: value, value };
          }

          if (typeof value === 'object') {
            return processInitialValueObjectAsAutocompleteOption(
              value as Record<string, unknown>,
              field,
            );
          }

          return null;
        });

        return values as AutocompleteOption[];
      }

      if (typeof field.defaultValue === 'object') {
        return processInitialValueObjectAsAutocompleteOption(
          field.defaultValue as Record<string, unknown>,
          field,
        );
      }

      if (typeof field.defaultValue === 'string') {
        return { label: field.defaultValue, value: field.defaultValue };
      }

      return null;
    }

    if (typeof field.defaultValue === 'string') {
      return { label: field.defaultValue, value: field.defaultValue };
    }

    if (Array.isArray(field.defaultValue)) {
      return field.defaultValue.map((value) => ({ label: value, value }) as AutocompleteOption);
    }

    return null;
  };

  const handleOnChange = (
    newValue: AutocompleteOption | AutocompleteOption[] | string | string[] | null,
    controllerField: ControllerRenderProps,
    field: ProfileFieldConfig,
  ) => {
    setSearchTerm('');

    if (!newValue) {
      controllerField.onChange(null);
      field.onChange?.([]);

      return;
    }

    // Convert to array, flatten, and process
    const normalizedArray = [newValue].flat();
    const parsed = normalizedArray.map((val) =>
      typeof val === 'string' ? { label: val, value: val } : val,
    );

    // For single value fields, use the first item; for multi, use the array
    const controllerValue = Array.isArray(newValue) ? parsed : parsed[0];

    controllerField.onChange(controllerValue);

    if (handleAsInstitutionOption(field)) {
      // Pass the complete objects to onChange handler
      field.onChange?.(parsed);

      return;
    }

    const mappedValues = parsed.map((opt) => opt.value);

    field.onChange?.(mappedValues);
  };

  const computedInitialValue = initialValue();

  // Use reset to set the value when we have data but the form doesn't (only once on mount)
  useEffect(() => {
    // Only apply initial value once when we first have data
    if (!hasAppliedInitialValue.current && computedInitialValue) {
      reset({ [field.key]: computedInitialValue }, { keepDefaultValues: true });
      hasAppliedInitialValue.current = true; // Mark as done to prevent future resets
    }
  }, [computedInitialValue, field.key, reset]); // Removed currentValue from dependencies

  return (
    <Box sx={autoCompleteSelectContainerSx} key={field.key}>
      <Controller
        key={`${field.key}-${computedInitialValue ? 'with-data' : 'empty'}`}
        name={field.key}
        control={control}
        defaultValue={computedInitialValue}
        render={({ field: controllerField, fieldState: { error: fieldError } }) => {
          const value = controllerField.value ?? '';

          const mergedOptions = (() => {
            if (!value) return formattedOptions;

            const currentValue = Array.isArray(value) ? value : [value];
            const valuesToCheck = currentValue.filter((v) => v && typeof v === 'object' && v.value);

            // Add any current values that aren't in the formatted options
            const missingOptions = valuesToCheck.filter(
              (v) => !formattedOptions.some((opt) => opt.value === v.value),
            );

            return [...missingOptions, ...formattedOptions];
          })();

          const isMulti = field.maxSelectedOptions !== 1;

          return (
            <>
              <Autocomplete
                freeSolo
                autoHighlight
                options={mergedOptions}
                value={value}
                sx={autoCompleteSx}
                disableClearable={isMulti}
                multiple={isMulti}
                disabled={disabled}
                popupIcon={<AppIcon iconName="ChevronDown" diameter="20px" color="Gray/400" />}
                onChange={(_, newValue) => handleOnChange(newValue, controllerField, field)}
                onInputChange={(_, input, reason) => {
                  if (reason === 'input') {
                    setSearchTerm(input);
                    if (!isMulti && input === '') {
                      controllerField.onChange(null);
                      field.onChange?.(['']);
                    }
                  }
                }}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : (option.label ?? option.value)
                }
                open={searchTerm.length >= minSearchLength}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id={`profile-api-autocomplete-${field.key}`}
                    label={undefined}
                    error={!!(error || fieldError)}
                    helperText={
                      error || (isMulti ? `Minimum ${minSearchLength} characters` : undefined)
                    }
                    placeholder={field.placeholder}
                    sx={textFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                      startAdornment: field.showSearch ? (
                        <Box sx={searchIconSx}>
                          <AppIcon diameter="24px" iconName="SearchMd" color="Gray/500" />
                        </Box>
                      ) : null,
                    }}
                    inputProps={{
                      ...params.inputProps,
                      onKeyDown: (e) => {
                        // prevent chip removal on backspace only in multi-mode
                        if (e.key === 'Backspace' && isMulti && !searchTerm) {
                          e.stopPropagation();
                        }
                      },
                      onBlur: (e) => {
                        const inputValue = e.target.value;

                        setSearchTerm('');
                        if (!isMulti) {
                          // Try to find a matching option from formattedOptions to preserve metadata
                          const matchingOption = formattedOptions.find(
                            (option) => option.label === inputValue || option.value === inputValue,
                          );

                          if (matchingOption) {
                            handleOnChange(matchingOption, controllerField, field);
                          } else {
                            handleOnChange(inputValue, controllerField, field);
                          }
                        }
                      },
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  return (
                    <Box component="li" {...props} sx={menuItemSx}>
                      <Box width="100%">{option.label}</Box>

                      {selected && (
                        <AppIcon
                          iconName="Check"
                          diameter="20px"
                          color="Colors/Foreground/fg-brand-primary"
                        />
                      )}
                    </Box>
                  );
                }}
              />

              {isMulti && Array.isArray(value) && (
                <Box sx={underneathTagContainerSx}>
                  {value.map((option, index) => (
                    <Chip
                      key={`${field.key}-${option.value}-${index}`}
                      label={
                        option.label.length > 40 ? option.label.slice(0, 40) + '...' : option.label
                      }
                      variant="tag"
                      size="md"
                      sx={chipSx}
                      onDelete={() => {
                        const newValue = value.filter((_, idx) => idx !== index);

                        controllerField.onChange(newValue);
                        field.onChange?.(
                          newValue.map((opt) =>
                            handleAsInstitutionOption(field) ? opt : opt.value,
                          ),
                        );
                      }}
                      deleteIcon={
                        <Box sx={deleteIconContainerStyle}>
                          <AppIcon
                            iconName="XClose"
                            diameter="12px"
                            color="Colors/Foreground/fg-quaternary"
                          />
                        </Box>
                      }
                    />
                  ))}
                </Box>
              )}
            </>
          );
        }}
      />
    </Box>
  );
};

const autoCompleteSelectContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '100%',
  maxWidth: { xs: '70vw', md: '100%' },
};

const underneathTagContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
};

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const deleteIconContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  margin: `${backrs.spacing['spacing-none']} !important`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const searchIconSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: { xs: backrs.spacing['spacing-none'] },
  display: 'flex',
});

const autoCompleteSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-sm'],

  '& .MuiAutocomplete-root': {
    paddingY: `${backrs.spacing['spacing-none']} !important`,
  },
  '& .MuiAutocomplete-inputRoot': {
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    paddingY: `${backrs.spacing['spacing-none']} !important`,
    paddingX: { xs: `${backrs.spacing['spacing-sm']} !important` },

    gap: backrs.spacing['spacing-xs'],
    maxHeight: '40px',
    minHeight: '40px',
    height: '40px',
  },

  '& .MuiAutocomplete-endAdornment': {
    top: '24px',
  },
  '& .MuiAutocomplete-tag': {
    margin: `${backrs.spacing['spacing-none']}`,
  },
  '& .MuiAutocomplete-popupIndicator': {
    transition: 'transform 0.3s ease',
  },
});

const menuItemSx: SxProps<Theme> = ({ backrs }) => ({
  transition: 'all 0.3s ease-in-out',

  '&.Mui-focused, &:hover, &.Mui-focusVisible, &.Mui-selected, &.Mui-active, &.MuiAutocomplete-option[aria-selected="true"], &[data-focus="true"]':
    {
      backgroundColor: `${backrs.colors['Colors/Background/bg-active']} !important`,
    },
});

const textFieldSx: SxProps<Theme> = {
  // we don't want to show the X, but we need to enable it for the clearable prop to work
  '& .MuiAutocomplete-clearIndicator': {
    visibility: 'hidden !important',
    display: 'none !important',
    color: 'transparent',
    cursor: 'default',
    '&:hover': {
      color: 'transparent',
      backgroundColor: 'transparent',
    },
  },
};
